import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Settings, User, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export const Navbar = ({ cartItemCount = 0 }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex items-center gap-0.5">
            <div className="w-3 h-3 rounded-full bg-accent" />
            <div className="w-3 h-3 rounded-full bg-cast" />
            <div className="w-3 h-3 rounded-full bg-craft" />
          </div>
          <span className="font-sans font-bold text-base tracking-[0.18em] text-primary">
            KASTIQ
          </span>
        </Link>

        {/* Nav links */}
        <div className="hidden md:flex items-center gap-7">
          <Link
            to="/"
            className="text-sm text-foreground hover:text-accent transition-colors"
          >
            Home
          </Link>
          <button
            onClick={() => {
              if (window.location.pathname === "/") {
                document
                  .getElementById("how-it-works")
                  ?.scrollIntoView({ behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            className="text-sm text-foreground hover:text-accent transition-colors bg-transparent border-none cursor-pointer p-0"
          >
            Services
          </button>
          <button
            onClick={() => {
              if (window.location.pathname === "/") {
                document
                  .getElementById("upload-section")
                  ?.scrollIntoView({ behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            className="text-sm text-foreground hover:text-accent transition-colors bg-transparent border-none cursor-pointer p-0"
          >
            Start Order
          </button>
          {isAuthenticated && (
            <Link
              to="/profile"
              className="text-sm text-foreground hover:text-accent transition-colors"
            >
              My Orders
            </Link>
          )}
        </div>

        {/* Right: Auth + Cart */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/profile")}
                className="flex items-center gap-1.5 text-sm"
                data-testid="nav-profile-button"
              >
                <User className="w-4 h-4" />
                <span className="hidden md:inline">{user?.first_name}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-destructive hover:text-destructive hover:bg-red-50"
                data-testid="nav-logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                className="text-sm"
                data-testid="nav-login-button"
              >
                Sign in
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register")}
                className="bg-accent hover:bg-accent-hover text-white text-sm px-4"
                data-testid="nav-register-button"
              >
                Create account
              </Button>
            </>
          )}

          {/* Cart */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/cart")}
            className="relative ml-1"
            data-testid="nav-cart-button"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartItemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </Button>

          {user?.is_admin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              data-testid="nav-admin-button"
            >
              <Settings className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-white border-t border-border py-12 mt-0">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-10 mb-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-0.5">
                <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                <div className="w-2.5 h-2.5 rounded-full bg-cast" />
                <div className="w-2.5 h-2.5 rounded-full bg-craft" />
              </div>
              <span className="font-sans font-bold text-sm tracking-[0.18em] text-primary">
                KASTIQ
              </span>
            </div>
            <p className="text-xs text-muted-foreground tracking-wider mb-1">
              Fine Jewelry Casting
            </p>
            <p className="text-xs text-muted-foreground">
              <span className="text-accent font-semibold">UPLOAD</span>
              {" · "}
              <span className="text-cast font-semibold">CAST</span>
              {" · "}
              <span className="text-craft font-semibold">DELIVER</span>
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-sans font-semibold text-xs uppercase tracking-widest mb-4 text-foreground">
              CONTACT
            </h4>
            <p className="text-sm text-muted-foreground mb-1">+1 (555) 123-4567</p>
            <p className="text-sm text-muted-foreground mb-1">support@kastiq.com</p>
            <p className="text-sm text-muted-foreground">Los Angeles, CA 90015</p>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-sans font-semibold text-xs uppercase tracking-widest mb-4 text-foreground">
              SERVICES
            </h4>
            <ul className="space-y-2">
              {[
                "Lost-Wax Casting",
                "Precious Metal Casting",
                "Finishing & Polish",
                "Stone Setting Prep",
                "Start a Custom Order",
              ].map((s) => (
                <li key={s} className="text-sm text-muted-foreground">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-6 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            © 2026 Kastiq LLC. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
